import { defineComponent as _defineComponent } from 'vue'
import BankAccountEdit from "@/components/BankAccountEdit.vue";

import ContractEdit from "@/components/ContractEdit.vue";

import LogoEdit from "@/components/LogoEdit.vue";

import AddressEdit from "@/components/AddressEdit.vue";

import EmployeeEdit from "@/components/EmployeeEdit.vue";

import PortalEdit from "@/components/PortalEdit.vue";

import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";

import {
  computed,
  onMounted,
  PropType,
  ref,
  unref,
  watch
} from 'vue'

import {
  useI18n,
} from "@/composables/useI18n";

import {
  Issuer,
} from "@/model/Issuer";

import {
  IssuerStatus,
  PaymentMethod,
  PersonalizationType,
  WebChargeInterval,
} from "@/Enums";

import {
  ApiResponse,
  httpMethod,
} from "@/scripts/Common/api";

import InformationDialog from "@/components/base/InformationDialog.vue";

import {
  createOrUpdateIssuer,
  fetchIssuerCards,
  fetchIssuers,
  updateIssuerState,
} from "@/components/issuers/scripts/issuers/requests";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  isRequired,
  isStringAndHasLength,
} from "@/scripts/Common/validators/defaultValidator";

import {
  hasValidLengthOnSepaCondition,
  hasSepaCondition,
} from "@/scripts/Common/validators";

import {
  SEPA_CHARACTER_REPLACER_REGEX,
} from "@/scripts/Constants";

import {
  countCharacterSepaField,
  getInvalidCharacters
} from "@/scripts/Common/helpers";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editIssuersPolicy,
} from "@/authorization/policies";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import {
  ISSUER_ID_STORAGE_KEY,
} from "@/components/issuers/scripts";

import {
  PaymentDetails,
} from "@/components/children";

import {debounce} from "@/scripts/Common/HelperFunctions";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS
} from "@/components/issuers/scripts/constants";

import {
  loadIssuerFromStorageOrFirst
} from "./scripts/issuers/helper";

import {
  companyNameLength,
} from "@/scripts/Common/constraints/constraints";


export default /*#__PURE__*/_defineComponent({
  __name: 'IssuerCoreData',
  props: {
  startInCreationMode: {
    required: true,
    type: Boolean as PropType<boolean>
  }
},
  emits: [
  'onFetchError'
],
  setup(__props, { emit: emits }) {

const props = __props;

const {
  t,
} = useI18n();





const comboSelection = ref<Issuer | undefined>(undefined);
const issuerSearchInput = ref<string | undefined>(undefined);
const issuers = ref<Issuer[]>([]);
const selectedIssuer = ref<Issuer>(new Issuer());

const paycardForm = ref();
const websiteForm = ref();
const issuerForm = ref();

const contactAddressValid = ref<boolean>(true);
const businessAddressValid = ref<boolean>(true);
const contactEmployeeValid = ref<boolean>(true);
const businessBankAccountValid = ref<boolean>(true);
const issuerCardNumberValid = ref<boolean>(true);
const startBeforeEndDate = ref<boolean>(true);
const websiteLogoValid = ref<boolean>(true);
const websiteValid = ref<boolean>(true);
const paycardLogoValid = ref<boolean>(true);
const paycardValid = ref<boolean>(true);
const paymentDetailsValid = ref<boolean>(true);
const hasActivePaycards = ref<boolean>(false);
const valid = ref<boolean>(true);

const informationHeadline = ref(t('Dialogs.Information.Headline'));
const informationText = ref("");
const informationDialog = ref();

const panelExpander = ref([1]);
const websiteExpander = ref([1]);
const paycardsExpander = ref([1]);
const expandBusinessBankAccount = ref<number[]>([1]);

const isCreationMode = ref<boolean>(false);

let ignoreIssuerSearchInputChange = false; // when the issuer is selected from the combobox, the issuerSearchInput change that follows should not trigger a reload of the issuer list

const hasNoEditRights = computed(() => {
  return !hasAccessRights(editIssuersPolicy) || selectedIssuer.value.Status == IssuerStatus.Canceled;
});

const hasIssuers = computed(() => {
  return issuers.value && issuers.value.length > 0;
});

const baseDataIsValid = computed(() => {
  return startBeforeEndDate.value
    && contactAddressValid.value
    && businessAddressValid.value
    && contactEmployeeValid.value
    && businessBankAccountValid.value
    && paycardLogoValid.value
    && paycardValid.value
    && websiteLogoValid.value
    && websiteValid.value
    && issuerCardNumberValid.value
    && paymentDetailsValid.value
    && valid.value;
});

const canReset = computed(() => {
  return selectedIssuer.value.HasChanges || (hasIssuers.value && isCreationMode.value);
})

const canSave = computed(() => {
  return baseDataIsValid.value
    && selectedIssuer.value.HasChanges;
});

const canContract = computed(() => {
  return baseDataIsValid.value
    && selectedIssuer.value.Status === IssuerStatus.Interested
    && !!selectedIssuer.value.Contract.Start;
});

const canConfirm = computed(() => {
  return baseDataIsValid.value
    && hasActivePaycards.value
    && selectedIssuer.value.Status === IssuerStatus.Contracted
    && !!selectedIssuer.value.Contract.Start;
});

const canCancel = computed(() => {
  return baseDataIsValid.value
    && selectedIssuer.value.Status === IssuerStatus.Confirmed
    && !!selectedIssuer.value.Contract.Start
    && !!selectedIssuer.value.Contract.End;
});

const bankAccountIsOptional = computed(() => {
  return selectedIssuer.value.PaymentDetails.paymentMethod === PaymentMethod.invoice;
});

const webChargeIntervals = [
  {id: WebChargeInterval.Once, value: t("WebChargeInterval.Once")},
  {id: WebChargeInterval.Unknown, value: t("WebChargeInterval.Unknown")},
  {id: WebChargeInterval.Monthly, value: t("WebChargeInterval.Monthly")},
];

const paycardPersonalizationMode = [
  {id: PersonalizationType.Text, value: t("PaycardPersonalizationMode.Text")},
  {id: PersonalizationType.LogoPart, value: t("PaycardPersonalizationMode.LogoPart")},
  {id: PersonalizationType.LogoFull, value: t("PaycardPersonalizationMode.LogoFull")},
];

const GetIssuerStateChangerButton = (status: IssuerStatus | undefined) => {
  switch (status) {
    case IssuerStatus.Interested:
      return {
        text: t("Button.SetStateContracted"),
        icon: "mdi-file-document-edit",
        condition: !canContract.value || hasNoEditRights.value
      }
    case IssuerStatus.Contracted:
      return {
        text: t("Button.SetStateConfirmed"),
        icon: "mdi-account-reactivate",
        condition: !canConfirm.value || hasNoEditRights.value
      }
    case IssuerStatus.Confirmed:
      return {
        text: t("Button.SetStateCanceled"),
        icon: "mdi-archive-cancel-outline",
        condition: !canCancel.value || hasNoEditRights.value
      }
    default:
      return {text: t("Button.StateNotFound"), icon: "mdi-close-thick", condition: true}
  }
};

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
};

const onHandleInformationDialogConfirm = () => {
  informationHeadline.value = t('Dialogs.Information.Headline');
  informationText.value = "";
}

const loadIssuers = async () => {

  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if (issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);
  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
}

const setUpEmptyPageForCreation = () => {
  isCreationMode.value = true;
  selectedIssuer.value = new Issuer();
}

const translateState = (status: IssuerStatus | undefined) => {
  switch (status) {
    case IssuerStatus.Interested:
      return t("State.Interested");
    case IssuerStatus.Contracted:
      return t("State.Contracted");
    case IssuerStatus.Confirmed:
      return t("State.Confirmed");
    case IssuerStatus.Canceled:
      return t("State.Canceled");
    default:
      return t("State.Unknown");
  }
}

const hasIssuerCards = async () => {
  if (selectedIssuer.value.IsNew) return false;
  if (!selectedIssuer.value.Id) return false;

  const {
    response,
    error,
  } = await fetchIssuerCards(selectedIssuer.value.Id);

  if (error) {
    onHandleFetchError({error});
    return false;
  }

  return (response ?? []).length > 0;
};

const resetItem = () => {
  selectedIssuer.value.Reset()
  reloadIssuer();
  validate();
}

const saveItem = async () => {
  const {error, response} = selectedIssuer.value.IsNew
    ? await createOrUpdateIssuer(unref(selectedIssuer) as Issuer, httpMethod.POST)
    : await createOrUpdateIssuer(unref(selectedIssuer) as Issuer, httpMethod.PUT);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  reloadIssuer(response?.issuerId);
}

const changeState = async (state: IssuerStatus | undefined) => {
  let apiResponse: ApiResponse<number> = {error: undefined, response: undefined};

  switch (state) {
    case IssuerStatus.Interested:
      apiResponse = await updateIssuerState(unref(selectedIssuer) as Issuer, "contract");
      break;
    case IssuerStatus.Contracted:
      apiResponse = await updateIssuerState(unref(selectedIssuer) as Issuer, "confirm");
      break;
    case IssuerStatus.Confirmed :
      apiResponse = await updateIssuerState(unref(selectedIssuer) as Issuer, "cancel");
      break;
  }

  if (apiResponse.error) {
    onHandleFetchError({error: apiResponse.error});
    return;
  }

  if (apiResponse.response) {
    saveItemToStorage(apiResponse.response, ISSUER_ID_STORAGE_KEY);
  }

  if (isCreationMode.value) isCreationMode.value = false;

  reloadIssuer();
}

const reloadIssuer = async (issuerId: number = 0) => {
  if (issuerId) {
    saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY);
  }

  const {response, error} = await loadIssuerFromStorageOrFirst();

  if(error) {
    onHandleFetchError({error});
    return;
  }

  // if no issuer is found
  if (!response) {
    setUpEmptyPageForCreation();
    return;
  }

  selectedIssuer.value = response;
  comboSelection.value = selectedIssuer.value;
  isCreationMode.value = false;
}

const validate = () => {
  websiteForm.value?.validate();
  paycardForm.value?.validate();
  issuerForm.value?.validate();
}

watch(() => comboSelection.value, () => {
  if (comboSelection.value && comboSelection.value instanceof Issuer) {
    selectedIssuer.value = comboSelection.value;
  }
});

watch([valid, websiteValid, paycardForm], () => {
  validate();
})

watch(() => selectedIssuer.value.PaymentDetails.paymentMethod, () => {
  const isNullOrInvoice = selectedIssuer.value.PaymentDetails.paymentMethod === PaymentMethod.invoice ||
    selectedIssuer.value.PaymentDetails.paymentMethod === null;

  if (isNullOrInvoice) return;

  if (selectedIssuer.value.HasChangedPaymentMethod()) {
    expandBusinessBankAccount.value = [0];
  }
  if (selectedIssuer.value.BusinessBankAccount.isModelEmpty()) return;
  if (!selectedIssuer.value.HasChangedPaymentMethod()) return;

  informationHeadline.value = t('Dialogs.Information.PaymentMethodHeadline');
  informationText.value = t('Dialogs.Information.PaymentMethodText');
  informationDialog?.value.Show();
});

const onHandleSelectionChange = () => {
  const issuerId = comboSelection.value?.Id; //  comboSelection.value could be something else than an Issuer (string companyname without results)

  if (!issuerId || !(comboSelection.value instanceof Issuer)) return;
  ignoreIssuerSearchInputChange = true; // avoid a reload of the issuer list when the issuer is selected from the combobox
  saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY);
}

watch(() => issuerSearchInput.value, debounce(async () => {
  if (ignoreIssuerSearchInputChange) {
    ignoreIssuerSearchInputChange = false;
    return;
  }
  await loadIssuers();
}));

const initializeIssuer = async () => {
  if (props.startInCreationMode) {
    setUpEmptyPageForCreation();
    return;
  }

  const {response, error} = await loadIssuerFromStorageOrFirst();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  // if no issuer is found, start creation mode
  if (!response) {
    setUpEmptyPageForCreation();
    return;
  }

  selectedIssuer.value = response;
  comboSelection.value = response;
}

onMounted(async () => {
  await initializeIssuer();

  // TODO: Will be refactored when F&E has been defined https://tonfunk.atlassian.net/browse/PROFI-5987
  hasActivePaycards.value = await hasIssuerCards();
});


return { __sfc: true,t, props, emits, comboSelection, issuerSearchInput, issuers, selectedIssuer, paycardForm, websiteForm, issuerForm, contactAddressValid, businessAddressValid, contactEmployeeValid, businessBankAccountValid, issuerCardNumberValid, startBeforeEndDate, websiteLogoValid, websiteValid, paycardLogoValid, paycardValid, paymentDetailsValid, hasActivePaycards, valid, informationHeadline, informationText, informationDialog, panelExpander, websiteExpander, paycardsExpander, expandBusinessBankAccount, isCreationMode, ignoreIssuerSearchInputChange, hasNoEditRights, hasIssuers, baseDataIsValid, canReset, canSave, canContract, canConfirm, canCancel, bankAccountIsOptional, webChargeIntervals, paycardPersonalizationMode, GetIssuerStateChangerButton, onHandleFetchError, onHandleInformationDialogConfirm, loadIssuers, setUpEmptyPageForCreation, translateState, hasIssuerCards, resetItem, saveItem, changeState, reloadIssuer, validate, onHandleSelectionChange, initializeIssuer, BankAccountEdit, ContractEdit, LogoEdit, AddressEdit, EmployeeEdit, PortalEdit, FloatingActionButton, IssuerStatus, PersonalizationType, WebChargeInterval, InformationDialog, isRequired, isStringAndHasLength, hasValidLengthOnSepaCondition, hasSepaCondition, SEPA_CHARACTER_REPLACER_REGEX, countCharacterSepaField, getInvalidCharacters, hasAccessRights, editIssuersPolicy, PaymentDetails, companyNameLength }
}

})